import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { warehouseService } from "~/services/wms/warehouseService";
import type {
  HallwayFormData,
  LocationFilters,
  PositionFormData,
  RackFormData,
  WarehouseFormData,
} from "~/types/wms";

// Query keys
const WAREHOUSES_KEY = "wms_warehouses";
const HALLWAYS_KEY = "wms_hallways";
const RACKS_KEY = "wms_racks";
const POSITIONS_KEY = "wms_positions";
const LOCATION_TREE_KEY = "wms_location_tree";

// ============================================================================
// Warehouses
// ============================================================================

export function useWarehouses() {
  return useQuery({
    queryKey: [WAREHOUSES_KEY],
    queryFn: () => warehouseService.getWarehouses(),
  });
}

export function useWarehouse(id: string | undefined) {
  return useQuery({
    queryKey: [WAREHOUSES_KEY, id],
    queryFn: () => warehouseService.getWarehouseById(id!),
    enabled: !!id,
  });
}

export function useCreateWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WarehouseFormData) =>
      warehouseService.createWarehouse(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WAREHOUSES_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

export function useUpdateWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<WarehouseFormData>;
    }) => warehouseService.updateWarehouse(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WAREHOUSES_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

export function useDeleteWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => warehouseService.deleteWarehouse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WAREHOUSES_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

// ============================================================================
// Hallways
// ============================================================================

export function useHallways(warehouseId?: string) {
  return useQuery({
    queryKey: [HALLWAYS_KEY, warehouseId],
    queryFn: () => warehouseService.getHallways(warehouseId),
  });
}

export function useHallway(id: string | undefined) {
  return useQuery({
    queryKey: [HALLWAYS_KEY, "detail", id],
    queryFn: () => warehouseService.getHallwayById(id!),
    enabled: !!id,
  });
}

export function useCreateHallway() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: HallwayFormData) => warehouseService.createHallway(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HALLWAYS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

export function useUpdateHallway() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<HallwayFormData>;
    }) => warehouseService.updateHallway(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HALLWAYS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

export function useDeleteHallway() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => warehouseService.deleteHallway(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HALLWAYS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

// ============================================================================
// Racks
// ============================================================================

export function useRacks(hallwayId?: string) {
  return useQuery({
    queryKey: [RACKS_KEY, hallwayId],
    queryFn: () => warehouseService.getRacks(hallwayId),
  });
}

export function useRack(id: string | undefined) {
  return useQuery({
    queryKey: [RACKS_KEY, "detail", id],
    queryFn: () => warehouseService.getRackById(id!),
    enabled: !!id,
  });
}

export function useCreateRack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RackFormData) => warehouseService.createRack(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RACKS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

export function useUpdateRack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RackFormData> }) =>
      warehouseService.updateRack(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RACKS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

export function useDeleteRack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => warehouseService.deleteRack(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RACKS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

// ============================================================================
// Positions
// ============================================================================

export function usePositions(rackId?: string) {
  return useQuery({
    queryKey: [POSITIONS_KEY, rackId],
    queryFn: () => warehouseService.getPositions(rackId),
  });
}

export function usePosition(id: string | undefined) {
  return useQuery({
    queryKey: [POSITIONS_KEY, "detail", id],
    queryFn: () => warehouseService.getPositionById(id!),
    enabled: !!id,
  });
}

export function useCreatePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PositionFormData) =>
      warehouseService.createPosition(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSITIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

export function useUpdatePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<PositionFormData>;
    }) => warehouseService.updatePosition(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSITIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

export function useDeletePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => warehouseService.deletePosition(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSITIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

export function useCreatePositionsForRack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rackId, levels }: { rackId: string; levels: number }) =>
      warehouseService.createPositionsForRack(rackId, levels),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSITIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

export function useCreateRacksForHallway() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ hallwayId, count }: { hallwayId: string; count: number }) =>
      warehouseService.createRacksForHallway(hallwayId, count),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RACKS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LOCATION_TREE_KEY] });
    },
  });
}

// ============================================================================
// Location Tree
// ============================================================================

export function useLocationTree() {
  return useQuery({
    queryKey: [LOCATION_TREE_KEY],
    queryFn: () => warehouseService.getLocationTree(),
  });
}

// ============================================================================
// All Locations (Flat List)
// ============================================================================

const ALL_LOCATIONS_KEY = "wms_all_locations";

export function useAllLocations(filters?: LocationFilters) {
  return useQuery({
    queryKey: [ALL_LOCATIONS_KEY, filters],
    queryFn: () => warehouseService.getAllLocations(filters),
  });
}
